import { gql, useQuery } from '@apollo/client'
import flatten from 'lodash/flatten'
import { useMemo, useState } from 'react'

import {
  ConsoleQuantityManagementsQuery,
  ConsoleQuantityManagementsQueryVariables,
  EventCollectionPickupTimeFragmentDoc,
  PickupTimeItemsAggregationDetailFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { DateTime } from '@/core/entities'
import { dayjs } from '@/lib/dayjs'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const [pickupDate, setPickupDate] = useState<Date>(
    dayjs().startOf('d').toDate()
  )
  const { router } =
    useRouter<{ eventId: string; collectionId: string; type: string }>()

  const { data, loading, refetch } = useQuery<
    ConsoleQuantityManagementsQuery,
    ConsoleQuantityManagementsQueryVariables
  >(QUERY, {
    variables: {
      eventId: router.query.eventId,
      collectionId: router.query.collectionId,
      pickupDate: convertToUtc(dayjs().toDate()),
    },
    returnPartialData: true,
  })

  const eventCollection = useMemo(
    () =>
      data?.event?.eventCollections.edges.find(
        ({ node }) => node.collectionId === router.query.collectionId
      )?.node,
    [data?.event, router.query.collectionId]
  )

  const artistAggregatePickupTimeItems = useMemo<
    State['artistAggregatePickupTimeItems']
  >(() => {
    if (!data?.artistAggregatePickupTimeItems) {
      return null
    }
    let newData: NonNullable<State['artistAggregatePickupTimeItems']> = []
    data?.artistAggregatePickupTimeItems?.forEach(aggregatePickupTime => {
      if (!aggregatePickupTime.pickupTime) {
        return null
      }
      //check select pickupTime not same aggregatePickupTime.pickupTime
      if (
        dayjs(aggregatePickupTime.pickupTime).format('YYYY/MM/DD HH:mm:ss') !==
          dayjs(pickupDate).format('YYYY/MM/DD HH:mm:ss') &&
        dayjs(pickupDate).format('HH:mm:ss') !== '00:00:00'
      ) {
        return null
      }
      if (!aggregatePickupTime.items?.filter(i => !!i).length) {
        return null
      }
      newData = newData.concat([
        DateTime.formatStartEndTime(
          aggregatePickupTime.pickupTime,
          eventCollection?.validMinutes
        ),
        ...flatten(
          (aggregatePickupTime.items?.filter(i => !!i) || []).map(item => {
            if (item?.skus?.length) {
              return item.skus.map(sku => ({ ...item, sku }))
            }
            return item
          })
        ),
      ])
    })
    return newData
  }, [data?.artistAggregatePickupTimeItems, eventCollection, pickupDate])

  return {
    state: {
      artistId: user?.uid ?? '',
      loading,
      pickupDate,
      eventCollection,
      eventName: data?.event?.title ?? '',
      artistAggregatePickupTimeItems,
    },
    actions: {
      async onRefetch(val) {
        setPickupDate(val ?? pickupDate)
        await refetch({
          eventId: router.query.eventId,
          collectionId: router.query.collectionId,
          pickupDate: convertToUtc(dayjs(val ?? pickupDate).toDate()),
        })
      },
    },
  }
})

const QUERY = gql`
  query ConsoleQuantityManagements(
    $collectionId: String!
    $eventId: ID!
    $pickupDate: Date!
  ) {
    artistAggregatePickupTimeItems(
      eventId: $eventId
      collectionId: $collectionId
      pickupDate: $pickupDate
    ) {
      ...PickupTimeItemsAggregationDetail
    }
    event(id: $eventId) {
      id
      title
      eventCollections(pageInput: { limit: 30 }) {
        edges {
          node {
            ...EventCollectionPickupTime
          }
        }
      }
    }
  }
  ${PickupTimeItemsAggregationDetailFragmentDoc}
  ${EventCollectionPickupTimeFragmentDoc}
`

function convertToUtc(dateTime: string | Date): string {
  return dayjs(dateTime).startOf('d').format('YYYY-MM-DDT00:00:00.000') + 'Z'
}
